/**
 * Handbook entry `timestamps`: string list (DB JSONB) for YouTube cue points / comment references.
 */

/** Parse flexible timestamp strings to whole seconds. */
export function parseTimestampToSeconds(input) {
  if (input == null) return 0;
  const s = String(input).trim();
  if (!s) return 0;
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }
  const tParam = s.match(/[?&]t=(\d+)/i);
  if (tParam) return parseInt(tParam[1], 10) || 0;
  const parts = s.split(':').map((p) => {
    const x = parseFloat(String(p).trim());
    return Number.isFinite(x) ? x : NaN;
  });
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) return Math.floor(parts[0] * 3600 + parts[1] * 60 + parts[2]);
  if (parts.length === 2) return Math.floor(parts[0] * 60 + parts[1]);
  return 0;
}

/** Display as HH:MM:SS (always two-digit hours segment per product request). */
export function formatTimestampHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/**
 * @param {string[] | null | undefined} raw
 * @returns {{ index: number, label: string, seconds: number, displayHms: string }[]}
 */
export function buildHandbookMoveOptions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry, i) => {
    const seconds = parseTimestampToSeconds(entry);
    const displayHms = formatTimestampHMS(seconds);
    return {
      index: i + 1,
      label: `Move ${i + 1} ${displayHms}`,
      seconds,
      displayHms,
    };
  });
}

/** Add YouTube iframe `start` (seconds) for /embed/ URLs. */
export function appendYoutubeStartSeconds(embedUri, seconds) {
  if (!embedUri || typeof embedUri !== 'string') return embedUri;
  if (!Number.isFinite(seconds) || seconds < 0) return embedUri;
  if (!/youtube\.com\/embed\//i.test(embedUri)) return embedUri;
  try {
    const u = new URL(embedUri);
    u.searchParams.set('start', String(Math.floor(seconds)));
    return u.toString();
  } catch {
    return embedUri;
  }
}
